import { useState, type FormEvent } from "react";
import { apiPost } from "@/lib/api";
import { useToast } from "@/components/feature/ToastProvider";

interface ContactFormProps {
  memNo?: number | string;
  onSuccess?: () => void;
  compact?: boolean;
}

const CONTENT_MAX = 2000;
const NAME_MAX = 30;

// 문의 폼 (모달 · 페이지 공용)
// 실제 클럽 서버 API(contact_write.php)로 접수한다.
export default function ContactForm({ memNo, onSuccess, compact }: ContactFormProps) {
  const { toast } = useToast();

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [wname, setWname] = useState("");
  const [wtel, setWtel] = useState("");
  const [wemail, setWemail] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");

  const resetFields = () => {
    setWname("");
    setWtel("");
    setWemail("");
    setSubject("");
    setContent("");
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // 허니팟 검사 (봇이면 조용히 성공 처리, 서버로 전송하지 않음)
    const formData = new FormData(e.currentTarget);
    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    // 입력값 검증
    if (!wname.trim()) {
      setStatus("error");
      setErrorMsg("성명을 입력해 주세요.");
      return;
    }
    if (wname.trim().length > NAME_MAX) {
      setStatus("error");
      setErrorMsg(`성명은 ${NAME_MAX}자 이내로 입력해 주세요.`);
      return;
    }
    if (!content.trim()) {
      setStatus("error");
      setErrorMsg("문의내용을 입력해 주세요.");
      return;
    }
    if (content.length > CONTENT_MAX) {
      setStatus("error");
      setErrorMsg(`문의내용은 ${CONTENT_MAX}자 이내로 입력해 주세요.`);
      return;
    }

    setStatus("submitting");
    setErrorMsg("");

    try {
      const payload: Record<string, string | number | undefined> = {
        wname: wname.trim(),
        wtel: wtel.trim(),
        wemail: wemail.trim(),
        subject: subject.trim(),
        content: content.trim(),
      };
      // 회원 사업장 문의일 때만 mem_no 전송 (클럽 문의는 보내지 않는다)
      if (memNo != null && memNo !== "") {
        payload.mem_no = memNo;
      }

      const res = await apiPost("contact_write.php", payload);
      const msg = res.msg || "문의가 정상적으로 접수되었습니다.";

      setSuccessMsg(msg);
      setStatus("success");
      resetFields();
      toast(msg, "success");
      onSuccess?.();
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : "문의 접수에 실패했습니다. 잠시 후 다시 시도해 주세요.";
      setStatus("error");
      setErrorMsg(message);
      toast(message, "error");
    }
  };

  if (status === "success") {
    return (
      <div className="flex flex-col items-center justify-center py-14 text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
          <i className="ri-checkbox-circle-line text-3xl" aria-hidden="true"></i>
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
          문의가 정상적으로 접수되었습니다.
        </h3>
        <p className="mt-2 text-sm text-foreground-600">
          확인 후 연락드리겠습니다. 감사합니다.
        </p>
        {successMsg && successMsg !== "문의가 정상적으로 접수되었습니다." && (
          <p className="mt-1 text-xs text-foreground-500">{successMsg}</p>
        )}
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 whitespace-nowrap rounded-full border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100 cursor-pointer"
        >
          새 문의 작성
        </button>
      </div>
    );
  }

  const inputClass =
    "w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200";

  return (
    <form onSubmit={handleSubmit} data-readdy-form noValidate>
      {memNo != null && memNo !== "" && (
        <input type="hidden" name="mem_no" value={memNo} />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="wname" className="mb-1.5 block text-sm font-medium text-foreground-800">
            성명 <span className="text-accent-600">*</span>
          </label>
          <input
            id="wname"
            name="wname"
            type="text"
            required
            maxLength={NAME_MAX}
            value={wname}
            onChange={(e) => setWname(e.target.value)}
            placeholder="성명을 입력해 주세요"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="wtel" className="mb-1.5 block text-sm font-medium text-foreground-800">
            연락처
          </label>
          <input
            id="wtel"
            name="wtel"
            type="tel"
            inputMode="tel"
            value={wtel}
            onChange={(e) => setWtel(e.target.value)}
            placeholder="숫자와 하이픈만 (예: 010-0000-0000)"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="wemail" className="mb-1.5 block text-sm font-medium text-foreground-800">
          이메일
        </label>
        <input
          id="wemail"
          name="wemail"
          type="email"
          value={wemail}
          onChange={(e) => setWemail(e.target.value)}
          placeholder="이메일을 입력해 주세요"
          className={inputClass}
        />
      </div>

      <div className="mt-4">
        <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
          제목
        </label>
        <input
          id="subject"
          name="subject"
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="문의 제목을 입력해 주세요"
          className={inputClass}
        />
      </div>

      <div className="mt-4">
        <div className="mb-1.5 flex items-center justify-between">
          <label htmlFor="content" className="block text-sm font-medium text-foreground-800">
            문의내용 <span className="text-accent-600">*</span>
          </label>
          <span
            className={`text-xs ${
              content.length > CONTENT_MAX ? "text-accent-600" : "text-foreground-400"
            }`}
          >
            {content.length} / {CONTENT_MAX}
          </span>
        </div>
        <textarea
          id="content"
          name="content"
          required
          rows={compact ? 4 : 6}
          value={content}
          onChange={(e) => setContent(e.target.value.slice(0, CONTENT_MAX))}
          placeholder="문의 내용을 입력해 주세요 (최대 2000자)"
          className={`${inputClass} resize-none`}
        ></textarea>
      </div>

      {/* 안티스팸 허니팟 (서버로 전송되지 않습니다) */}
      <input
        type="text"
        name="website_alt"
        className="hp-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <label className="mt-4 flex items-start gap-2.5 text-sm text-foreground-700">
        <input
          type="checkbox"
          name="privacy_agree"
          value="Y"
          required
          className="mt-0.5 h-4 w-4 rounded border-background-300 text-primary-500 focus:ring-primary-300"
        />
        <span>
          개인정보 수집 및 이용에 동의합니다. <span className="text-accent-600">*</span>
        </span>
      </label>

      {status === "error" && errorMsg && (
        <p role="alert" className="mt-3 text-sm font-medium text-accent-700">
          {errorMsg}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 w-full whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer flex items-center justify-center gap-2"
      >
        {status === "submitting" && (
          <i className="ri-loader-4-line animate-spin text-lg" aria-hidden="true"></i>
        )}
        {status === "submitting" ? "접수 중…" : "문의 남기기"}
      </button>
    </form>
  );
}